import { createSlice } from "@reduxjs/toolkit";
import { staffApi } from "../../api/staffApi";
import { toast } from "../../components/common/toast";
import { reportDeleteFailure } from "../../utils/deleteFailure";
import type { AppDispatch, RootState } from "../../app/store";
import type { Staff } from "../../types/staff.types";
import { v4 as uuidv4 } from "uuid";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";
import { getApiErrorMessage } from "../../utils/apiError";
import {
  uploadMediaFiles,
  createResolvedMedia,
  buildRenamePayload,
  applyRename,
  buildDeletePayload,
  applyDelete,
  applyCover,
  buildCoverPayload,
} from "../../utils/mediaUtils";
import axios from "axios";
import { mediaLinkKey, resolveMediaLinks } from "../../utils/mediaLinkBatch";

interface StaffSearchPayload {
  pageIndex?: number;
  orderBy?: string;
  sortAscending?: boolean;
  sortDecending?: boolean;
  searchText?: string;
  ids?: string[];
  reportingManagerIds?: string[];
  businessUnitIds?: string[];
  departmentIds?: string[];
  organisationIds?: string[];
  memberTypes?: number[];
  isActive?: boolean | null;
  firstName?: string;
  lastName?: string;
  codes?: string[];
  cities?: string[];
  states?: string[];
  localites?: string[];
  emails?: string[];
  phones?: string[];
  roles?: string[];
  pageSize?: number;
  includeDeleted?: boolean;
}

// The newest first-page request (see fetchStaff).
let latestStaffPage = 0;

export const fetchStaff = (payload: StaffSearchPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().staff;
    const userData = getCurrentUser();
    // `??` (not `||`) so an explicit pageIndex of 0 isn't treated as absent.
    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;
    const targetPageSize = payload.pageSize ?? 10;
    // A first page always runs (a new search must not be dropped because an
    // earlier one is still loading); an answer to an older one is ignored.
    const isFirstPage = incomingPageIndex === 0;
    if ((state.isLoading || !state.hasMore) && !isFirstPage) return;
    const ticket = isFirstPage ? ++latestStaffPage : latestStaffPage;
    const superseded = () => ticket !== latestStaffPage;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: targetPageSize,
        pageIndex: incomingPageIndex,
        organisationIds: [`${(await userData).companyId}`],
        // "newest" maps to creationDate on the backend, so this listing was
        // ordered by when a record was created rather than when it was last
        // touched. Listings are work queues: most recently updated first.
        orderBy: payload.orderBy || "modificationdate",
        // "Sort by" from the listing (resolveSort gives sortAscending; this
        // endpoint takes the opposite flag).
        sortDecending: !(payload.sortAscending ?? false),
        searchText: payload.searchText || "",
        // For Super Admins, the backend ignores organisationIds/CompanyId
        // entirely and scopes solely by groupIds — an empty array here means
        // "no filter", i.e. every organisation's staff. Always pin it.
        groupIds: [`${(await userData).companyId}`],
        ids: payload.ids || [],
        reportingManagerIds: payload.reportingManagerIds || [],
        businessUnitIds: payload.businessUnitIds || [],
        departmentIds: payload.departmentIds || [],
        memberTypes: payload.memberTypes || [],
        isActive: payload.isActive ?? null,
        firstName: payload.firstName || "",
        lastName: payload.lastName || "",
        roles: payload.roles || [],
        codes: payload.codes || [],
        cities: payload.cities || [],
        states: payload.states || [],
        localites: payload.localites || [],
        emails: payload.emails || [],
        phones: payload.phones || [],
        // A lookup by id (a detail page, often opened fresh from the URL)
        // must find the record whatever its state: without this a deleted
        // record's page loaded once from the list and then came back empty
        // on reload, because the search defaults to excluding deleted rows.
        includeDeleted: payload.includeDeleted ?? !!payload.ids?.length,
      };

      const response = await staffApi.searchStaff(requestBody);
      const fetchedData: Staff[] = response.data || [];
      const totalCount = response.count || 0;

      if (superseded()) return;
      dispatch(setTotalStaffs(totalCount));

      // One media request per (organisation, type) for the whole page, not
      // one or two per member — pickers ask for up to 1000 members.
      const PROFILE_MEDIA_TYPE = 1;
      const ATTACHMENT_MEDIA_TYPE = 3;
      const links = await resolveMediaLinks(
        fetchedData,
        (s, want) => {
          if (s.profilePic && !s.profilePic.startsWith("http")) {
            want(s.groupId, PROFILE_MEDIA_TYPE, s.profilePic);
          }
          for (const media of s.medias || []) {
            want(s.groupId, ATTACHMENT_MEDIA_TYPE, media.s3Url);
          }
        },
        staffApi.getMediaLink,
        "Staff",
      );

      const resolvedData = fetchedData.map((staffMember: Staff) => {
        const updatedStaff = {
          ...staffMember,
          id: staffMember.id,
        };

        if (staffMember.profilePic) {
          updatedStaff.profilePicUrl = staffMember.profilePic.startsWith("http")
            ? staffMember.profilePic
            : links.get(
                mediaLinkKey(PROFILE_MEDIA_TYPE, staffMember.profilePic),
              ) || "";
        }

        if (staffMember.medias && staffMember.medias.length > 0) {
          updatedStaff.medias = staffMember.medias.map((media) => ({
            ...media,
            url:
              links.get(mediaLinkKey(ATTACHMENT_MEDIA_TYPE, media.s3Url)) || "",
          }));
          updatedStaff.mediaLinks = null;
        }

        return updatedStaff;
      });

      const currentLoadedCount =
        incomingPageIndex === 0
          ? resolvedData.length
          : state.items.length + resolvedData.length;

      if (superseded()) return;
      if (currentLoadedCount >= totalCount || resolvedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }
      if (incomingPageIndex === 0) {
        dispatch(setStaff(resolvedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendStaff(resolvedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
      return resolvedData;
    } catch {
      toast.error(
        i18n.t("staffs:sliceMessages.fetchError", {
          defaultValue: "Failed to fetch organisation members",
        }),
      );
    } finally {
      if (!superseded()) dispatch(setLoading(false));
    }
  };
};

export const addNewStaff = (formData: Partial<Staff>) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const res = await staffApi.createStaff(formData);
      const newStaff = { ...formData, id: res, isActive: true };
      const { items } = getState().staff;
      dispatch(setStaff([newStaff, ...items]));
      toast.success(
        i18n.t("staffs:sliceMessages.addSuccess", {
          defaultValue: "Organisation member added successfully",
        }),
      );
    } catch {
      toast.error(
        i18n.t("staffs:sliceMessages.addError", {
          defaultValue: "Failed to add organisation member",
        }),
      );
    }
  };
};

export const editStaff = (formData: Partial<Staff>) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const apiPayload = { ...formData };

    delete apiPayload.profilePicUrl;

    // Business Unit and Department are mandatory on a member — there is no
    // such thing as clearing them. An empty value here only ever means "this
    // edit didn't touch the field" (or a legacy row that never had one), so
    // the key is dropped: sending "" fails Guid binding with a 400, and
    // sending null would wipe a required value.
    if (!apiPayload.businessUnitId) delete apiPayload.businessUnitId;
    if (!apiPayload.departmentId) delete apiPayload.departmentId;

    // Reporting manager is optional and genuinely clearable, and it is Guid?
    // all the way down (EditMemberMessage, MemberEntity, column) — so null is
    // a valid value meaning "no manager". Dropping the key instead, as this
    // used to, made clearing impossible: the edit handler rebuilds the member
    // from the message, so an absent key left the old manager in place and it
    // came straight back on reload.
    if ("reportingManagerId" in apiPayload && !apiPayload.reportingManagerId) {
      apiPayload.reportingManagerId = null;
    }

    try {
      await staffApi.updateStaff(apiPayload);
    } catch (err) {
      console.error("Update Error:", err);
      // Re-thrown (with the backend's actual reason, when available) so
      // callers can show it instead of a generic message, and so they
      // don't optimistically apply a change the server rejected.
      throw new Error(
        getApiErrorMessage(
          err,
          i18n.t("staffs:sliceMessages.editError", {
            defaultValue: "Failed to update organisation member details",
          }),
        ),
      );
    }

    const { items } = getState().staff;

    const updatedList = items.map((item: Staff) =>
      item.id === formData.id ? { ...item, ...formData } : item,
    );

    dispatch(setStaff(updatedList));
    toast.success(
      i18n.t("staffs:sliceMessages.editSuccess", {
        defaultValue: "Organisation member details updated successfully",
      }),
    );
  };
};

export const uploadStaffImages = (
  memberId: string,
  files: File[],
  groupId?: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!files?.length) return;

      const state = getState();
      const userId = (await getCurrentUser()).userId;

      const { payloads, links } = await uploadMediaFiles(
        staffApi,
        memberId,
        files,
        groupId,
      );

      const newEntries = createResolvedMedia(payloads, links, memberId, userId);

      const updatedItems = state.staff.items.map((item: Staff) =>
        item.id === memberId
          ? {
              ...item,
              medias: [...(item.medias || []), ...newEntries],
              mediaLinks: null,
            }
          : item,
      );

      dispatch(setStaff(updatedItems));

      toast.success(
        i18n.t("staffs:sliceMessages.uploadSuccess", {
          count: files.length,
          defaultValue: `${files.length} images uploaded successfully`,
        }),
      );
    } catch (err) {
      console.error("Staff Upload Error:", err);

      toast.error(
        getApiErrorMessage(
          err,
          i18n.t("staffs:sliceMessages.uploadError", {
            defaultValue: "Failed to upload images",
          }),
        ),
      );
      throw err;
    }
  };
};

export const renameStaffImage = (
  memberId: string,
  fileId: string,
  newName: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const state = getState();

      const currentStaff = state.staff.items.find(
        (s: Staff) => s.id === memberId,
      );

      const targetFile = currentStaff?.medias?.find((m) => m.id === fileId);

      if (!targetFile) {
        toast.error(i18n.t("slices.shared.fileNotFound", { ns: "common" }));
        return;
      }

      const payload = buildRenamePayload(targetFile, memberId, newName);

      await staffApi.uploadMedia(memberId, payload);

      const updatedItems = state.staff.items.map((item: Staff) =>
        item.id === memberId
          ? {
              ...item,
              medias: applyRename(item.medias || [], fileId, newName),
            }
          : item,
      );

      dispatch(setStaff(updatedItems));

      toast.success(
        i18n.t("staffs:sliceMessages.renameSuccess", {
          defaultValue: "Image renamed successfully",
        }),
      );
    } catch (err) {
      console.error("Staff Rename Error:", err);

      toast.error(
        i18n.t("staffs:sliceMessages.renameError", {
          defaultValue: "Failed to rename image",
        }),
      );
    }
  };
};

/** Makes one of the member's photos their cover photo (null clears it). */
export const setStaffCover = (memberId: string, fileId: string | null) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { items } = getState().staff;
    const medias = items.find((s: Staff) => s.id === memberId)?.medias || [];
    const payload = buildCoverPayload(medias, memberId, fileId);
    if (payload.length === 0) return;
    try {
      await staffApi.uploadMedia(memberId, payload);
      dispatch(
        setStaff(
          items.map((item: Staff) =>
            item.id === memberId
              ? { ...item, medias: applyCover(item.medias || [], fileId) }
              : item,
          ),
        ),
      );
      toast.success(
        fileId
          ? i18n.t("slices.shared.coverUpdated", { ns: "common" })
          : i18n.t("slices.shared.coverRemoved", { ns: "common" }),
      );
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err,
          i18n.t("slices.shared.coverFailed", { ns: "common" }),
        ),
      );
      throw err;
    }
  };
};

export const removeStaffImage = (
  memberId: string,
  filesToRemove: Array<{ id: string }>,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!filesToRemove?.length) return;

      const state = getState();

      const currentStaff = state.staff.items.find(
        (s: Staff) => s.id === memberId,
      );

      if (!currentStaff?.medias) return;

      const idsToRemove = filesToRemove.map((f) => f.id);

      const payload = buildDeletePayload(
        currentStaff.medias,
        memberId,
        idsToRemove,
      );
      // Nothing to remove that this record still lists.
      if (payload.length === 0) return;

      await staffApi.uploadMedia(memberId, payload);

      const updatedItems = state.staff.items.map((item: Staff) =>
        item.id === memberId
          ? {
              ...item,
              medias: applyDelete(item.medias || [], idsToRemove),
            }
          : item,
      );

      dispatch(setStaff(updatedItems));

      toast.success(
        i18n.t("staffs:sliceMessages.deleteImageSuccess", {
          defaultValue: "Image removed successfully",
        }),
      );
    } catch (err) {
      console.error("Staff Remove Error:", err);

      toast.error(
        i18n.t("staffs:sliceMessages.deleteImageError", {
          defaultValue: "Failed to remove image",
        }),
      );
    }
  };
};

/**
 * The restored row as the server now has it (its active state comes from history), once the read model
 * has caught up: polled by id for a few seconds, then swapped into the list.
 */
async function refreshRestoredStaff(id: string, dispatch: AppDispatch, getState: () => RootState) {
  for (let attempt = 0; attempt < 5; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    try {
      const res = await staffApi.searchStaff({ pageIndex: 0, pageSize: 1, ids: [id], includeDeleted: true });
      const row = (res?.data ?? []).find((r: Staff) => r?.id === id);
      if (row && !row.deletedBy && !row.deletionDate) {
        const { items } = getState().staff;
        dispatch(setStaff(items.map((item: Staff) => (item.id === id ? { ...item, ...row } : item))));
        return;
      }
    } catch {
      return;
    }
  }
}

/**
 * Brings a removed member back (Super Admin only). The row leaves its deleted state at once; the
 * caller reloads the list for the restored active state, which the server reads from history.
 */
export const restoreStaff = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    try {
      await staffApi.restoreStaff(id);
      const { items } = getState().staff;
      dispatch(
        setStaff(
          items.map((item: Staff) =>
            item.id === id ? { ...item, deletedBy: undefined, deletedByName: undefined, deletionDate: undefined } : item,
          ),
        ),
      );
      toast.success(
        i18n.t("staffs:sliceMessages.restoreSuccess", { defaultValue: "Organisation member restored" }),
      );
      void refreshRestoredStaff(id, dispatch, getState);
      return true;
    } catch (err) {
      console.error("Error restoring staff", err);
      toast.error(
        getApiErrorMessage(err, i18n.t("staffs:sliceMessages.restoreError", { defaultValue: "Could not restore the member" })),
      );
      return false;
    }
  };
};

export const removeStaff = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await staffApi.removeStaff(id);

      const { items } = getState().staff;

      const filteredList = items.filter((item: Staff) => item.id !== id);

      dispatch(setStaff(filteredList));

      toast.success(
        i18n.t("staffs:sliceMessages.removeSuccess", {
          defaultValue: "Organisation member removed successfully",
        }),
      );
    } catch (err) {
      console.error("Error in deleting staff", err);
      // The API refuses while the member still holds in-progress contract
      // roles and says which — that opens the "Cannot delete" modal. Then
      // rethrown, not swallowed: a swallowed rejection here resolved as if
      // the member had been removed.
      reportDeleteFailure(
        err,
        i18n.t("staffs:sliceMessages.removeError", {
          defaultValue: "Failed to remove organisation member",
        }),
      );
      throw err;
    }
  };
};

export const handleActivate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await staffApi.activateStaff(id);

      const { items } = getState().staff;
      const updatedList = items.map((item) =>
        item.id === id ? { ...item, isActive: true } : item,
      );

      dispatch(setStaff(updatedList));

      toast.success(
        i18n.t("staffs:sliceMessages.statusSuccess", {
          defaultValue: "Organisation member status updated successfully",
        }),
      );
    } catch {
      toast.error(
        i18n.t("staffs:sliceMessages.statusError", {
          defaultValue: "Failed to update organisation member status",
        }),
      );
    }
  };
};

export const handleDeactivate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await staffApi.deActivateStaff(id);
      const { items } = getState().staff;
      const updatedList = items.map((item) =>
        item.id === id ? { ...item, isActive: false } : item,
      );

      dispatch(setStaff(updatedList));

      toast.success(
        i18n.t("staffs:sliceMessages.statusSuccess", {
          defaultValue: "Organisation member status updated successfully",
        }),
      );
    } catch {
      toast.error(
        i18n.t("staffs:sliceMessages.statusError", {
          defaultValue: "Failed to update organisation member status",
        }),
      );
    }
  };
};

export const uploadStaffProfilePic = async (
  file: File,
  groupId?: string,
): Promise<string> => {
  const fileId = uuidv4();
  const initRes = await staffApi.initUpload([fileId], groupId);

  const uploadUrl = initRes.links[0].m;

  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": file.type },
  });

  return fileId;
};

interface StaffState {
  items: Staff[];
  isLoading: boolean;
  pageIndex: number;
  hasMore: boolean;
  totalStaffs: number;
}

const initialState: StaffState = {
  items: [],
  isLoading: false,
  pageIndex: 0,
  hasMore: true,
  totalStaffs: 0,
};

const staffSlice = createSlice({
  name: "staff",
  initialState,
  reducers: {
    setStaff: (state, action) => {
      state.items = action.payload;
    },
    appendStaff: (state, action) => {
      state.items = [...state.items, ...action.payload];
    },
    setPageIndex: (state, action) => {
      state.pageIndex = action.payload;
    },
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    setTotalStaffs: (state, action) => {
      state.totalStaffs = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    resetStaffList: (state) => {
      state.items = [];
      state.pageIndex = 0;
      state.hasMore = true;
    },
  },
});

export const {
  setStaff,
  appendStaff,
  setPageIndex,
  setHasMore,
  resetStaffList,
  setLoading,
  setTotalStaffs,
} = staffSlice.actions;
export default staffSlice.reducer;
